import * as React from 'react'
import { useState } from 'react'
import { Search, X, ChevronDown, SlidersHorizontal, Calendar, Check } from 'lucide-react'
import type { Table } from '@tanstack/react-table'

export type DateFilter = 'all' | 'today' | 'week' | 'month' | 'year'

interface InvoiceToolbarProps<TData> {
  search: string
  onSearchChange: (value: string) => void
  dateFilter: DateFilter
  onDateFilterChange: (value: DateFilter) => void
  table: Table<TData>
}

const DATE_FILTER_OPTIONS: { value: DateFilter; label: string }[] = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'year', label: 'This Year' },
]

const COLUMN_LABELS: Record<string, string> = {
  select: 'Select',
  invoiceNo: 'Invoice No',
  invoiceDate: 'Date',
  patientName: 'Patient',
  grandTotal: 'Amount',
  status: 'Status',
  patientType: 'Type',
  branchName: 'Branch',
  paymentMethod: 'Payment',
  actions: 'Actions',
}

export function InvoiceToolbar<TData>({
  search,
  onSearchChange,
  dateFilter,
  onDateFilterChange,
  table,
}: InvoiceToolbarProps<TData>) {
  const [dateOpen, setDateOpen] = useState(false)
  const [columnsOpen, setColumnsOpen] = useState(false)

  const currentDateLabel = DATE_FILTER_OPTIONS.find(o => o.value === dateFilter)?.label || 'All Time'

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {/* Search Box - 10% smaller */}
      <div className="flex items-center gap-2 bg-card rounded-full px-3.5 py-2 w-full sm:w-[250px] border border-border transition-all focus-within:ring-2 focus-within:ring-info/20">
        <Search className="w-4 h-4 text-foreground flex-shrink-0" />
        <input
          type="text"
          placeholder="Search invoice, patient..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="border-none outline-none text-[13px] font-normal w-full bg-transparent text-text-dark placeholder:text-text-muted"
        />
        {search && (
          <button
            onClick={() => onSearchChange('')}
            className="w-4 h-4 rounded-full flex items-center justify-center hover:bg-hover"
          >
            <X className="w-3.5 h-3.5 text-text-muted" />
          </button>
        )}
      </div>

      {/* Filters - 10% smaller */}
      <div className="flex items-center gap-2.5">
        {/* Date Filter */}
        <div className="relative">
          <button
            onClick={() => setDateOpen(!dateOpen)}
            className="flex items-center justify-between gap-2 bg-card rounded-full px-3.5 py-2 w-[135px] border border-border transition-all hover:bg-hover"
          >
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-foreground flex-shrink-0" />
              <span className="text-[13px] font-normal text-text-dark">{currentDateLabel}</span>
            </div>
            <ChevronDown className={`w-4 h-4 text-foreground flex-shrink-0 transition-transform ${dateOpen ? 'rotate-180' : ''}`} />
          </button>

          {dateOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setDateOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-[135px] rounded-lg border border-border bg-card p-1 shadow-lg z-50">
                {DATE_FILTER_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onDateFilterChange(option.value)
                      setDateOpen(false)
                    }}
                    className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                      dateFilter === option.value
                        ? 'bg-hover text-text-dark font-medium'
                        : 'text-text-dark hover:bg-hover'
                    }`}
                  >
                    {option.label}
                    {dateFilter === option.value && (
                      <Check className="w-3.5 h-3.5 text-success" />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Column Selector */}
        <div className="relative">
          <button
            onClick={() => setColumnsOpen(!columnsOpen)}
            className="flex items-center justify-between gap-2 bg-card rounded-full px-3.5 py-2 w-[117px] border border-border transition-all hover:bg-hover"
          >
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-foreground flex-shrink-0" />
              <span className="text-[13px] font-normal text-text-dark">Columns</span>
            </div>
            <ChevronDown className={`w-4 h-4 text-foreground flex-shrink-0 transition-transform ${columnsOpen ? 'rotate-180' : ''}`} />
          </button>

          {columnsOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setColumnsOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-[162px] rounded-lg border border-border bg-card p-1 shadow-lg z-50">
                <div className="px-2.5 py-1.5 text-[11px] font-medium text-text-muted uppercase tracking-wide">
                  Toggle columns
                </div>
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => (
                    <button
                      key={column.id}
                      onClick={() => column.toggleVisibility(!column.getIsVisible())}
                      className={`flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                        column.getIsVisible()
                          ? 'text-text-dark'
                          : 'text-text-muted'
                      } hover:bg-hover`}
                    >
                      {COLUMN_LABELS[column.id] || column.id}
                      {column.getIsVisible() && (
                        <Check className="w-3.5 h-3.5 text-success" />
                      )}
                    </button>
                  ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
